import React, { useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import Loading from "../../../../components/Loading";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useQuery } from "react-query";
import { mutateErrors } from "../../../../utils";
import Error from "../../../../components/Error";
import PageHeader from "../../../../components/PageHeader";
import useServiceQueries from "../../hooks/useServiceQueries";
import { useHistory, useParams } from "react-router";
import Editor from "./Editor";
import Button from "../../../../components/Button";
import { ApplyButton } from "../../../../components/Adopter";
import { useApi } from "../../../../hooks/useApi";
import { useSnackbar } from "notistack";
import { Routes } from "../../../../constants/routes";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
}));

export interface ExtraServicePageImage {
  fileId: string;
  preview: string;
}

export interface ExtraServicePageImageSave {
  fileId: string;
}

export default function InfoPage() {
  const { request } = useApi();
  const classes = useStyles();
  const params = useParams<{ id: string }>();
  const { fetchDetails } = useServiceQueries();
  const { isLoading, isFetching, isError, error, data, refetch } =
    useQuery<any>(`service-page_${params.id}`, () => {
      return fetchDetails(params.id);
    });
  const [textData, setData] = useState(data?.data?.extraServicePage?.content);
  const [images, setImages] = useState<ExtraServicePageImage[]>(
    data?.data?.extraServicePage?.images || [],
  );
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const save = useCallback(() => {
    const id = data?.data?.extraServicePage?.id;
    const imagesSave: ExtraServicePageImageSave[] = [];

    images?.forEach((image) => {
      if (textData.indexOf(image.preview) !== -1) {
        imagesSave.push({ fileId: image.fileId });
      }
    });
    if (id) {
      try {
        return request({
          url: "/service-page",
          method: "PUT",
          data: {
            id: id,
            extraServiceId: params.id,
            content: textData,
            images: imagesSave,
          },
        }).then((res) => {
          enqueueSnackbar("Success", { variant: "success" });
          history.push(`/0/${Routes.SERVICES_ADMIN}`);
          refetch();
        });
      } catch (error) {
        enqueueSnackbar("Error", { variant: "error" });
      }
    } else {
      try {
        return request({
          url: "/service-page",
          method: "POST",
          data: {
            extraServiceId: params.id,
            content: textData,
            images: imagesSave,
          },
        }).then((res) => {
          enqueueSnackbar("Success", { variant: "success" });
          history.push(`/0/${Routes.SERVICES_ADMIN}`);
          refetch();
        });
      } catch (error) {
        enqueueSnackbar("Error", { variant: "error" });
      }
    }
  }, [textData, params.id]);

  useEffect(() => {
    if (data?.data?.extraServicePage?.content) {
      setData(data?.data?.extraServicePage?.content);
    }
  }, [data]);

  if (isLoading || isFetching) {
    return <Loading />;
  }

  if (isError) {
    const { message } = mutateErrors(error);
    return <Error text={message} />;
  }

  const addImage = (image: ExtraServicePageImage) => {
    setImages((prev) => [...prev, image]);
  };

  return (
    <>
      <Container maxWidth="lg">
        <PageHeader
          title={"Services"}
          subtitle={"Here are listed services"}
          endAdornment={
            <Button
              color={"success"}
              variant={"contained"}
              component={ApplyButton}
              onClick={save}
            >
              Save
            </Button>
          }
        />
      </Container>
      <Container maxWidth="lg" className={classes.container}>
        <Editor textData={textData} setData={setData} addImage={addImage} />
      </Container>
    </>
  );
}
